// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
// Consistent party identity + colour across every view. Colours are chosen to be
// recognisably party-aligned (Labor red, Liberal blue, Nationals bottle-green,
// Greens bright green) while staying mutually distinguishable.

export interface PartyStyle {
  code: string;
  name: string;
  colour: string;
}

const PARTY_STYLES: Record<string, PartyStyle> = {
  ALP: { code: 'ALP', name: 'Labor', colour: '#d8322f' },
  LIB: { code: 'LIB', name: 'Liberal', colour: '#1f5aa8' },
  LNP: { code: 'LNP', name: 'Liberal National (QLD)', colour: '#17427f' },
  NAT: { code: 'NAT', name: 'Nationals', colour: '#0c6e4e' },
  GRN: { code: 'GRN', name: 'Greens', colour: '#3aae3a' },
  ON: { code: 'ON', name: 'One Nation', colour: '#f07d20' },
  KAP: { code: 'KAP', name: "Katter's Australian Party", colour: '#9a5b2c' },
  CA: { code: 'CA', name: 'Centre Alliance', colour: '#12a3a3' },
  JLN: { code: 'JLN', name: 'Jacqui Lambie Network', colour: '#7c4bd0' },
  UAP: { code: 'UAP', name: 'United Australia Party', colour: '#e0a51a' },
  CLP: { code: 'CLP', name: 'Country Liberal (NT)', colour: '#b8541f' },
  IND: { code: 'IND', name: 'Independent', colour: '#5a6b7b' },
  FMR: { code: 'FMR', name: 'Former office-holder', colour: '#8a95a1' },
  OTH: { code: 'OTH', name: 'Other', colour: '#9aa5b1' },
};

export function partyStyle(code: string): PartyStyle {
  return PARTY_STYLES[code] ?? { code, name: code, colour: '#9aa5b1' };
}

export function partyColour(code: string): string {
  return partyStyle(code).colour;
}

// House of Representatives = green leather; Senate = red leather. Used as a
// semantic accent for chamber, distinct from party colour.
export function chamberColour(chamber: string): string {
  switch (chamber) {
    case 'House':
      return '#127a4f';
    case 'Senate':
      return '#a4243b';
    case 'Former PM':
      return '#8a6d3b';
    default:
      return '#6b7280';
  }
}
